import React, { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Section } from "@/components/ui/section";
import LoadingSkeleton from "./LoadingSkeleton";

interface PreloadFillProps {
    headerHeight?: number | string;
    footerHeight?: number | string;
    className?: string;
    sectionProps?: React.ComponentProps<typeof Section>;
    showLoader?: boolean; // Optional prop to control loader visibility
    loaderMessage?: string; // Optional message for the loader
}

/**
 * PreloadFill - fills the space between header and footer with a skeleton loader.
 * Keeps header and footer visually separated during loading.
 */

const PreloadFill: React.FC<PreloadFillProps> = ({
    headerHeight = 80,
    footerHeight = 80,
    className = "",
    sectionProps = {},
    showLoader = false,
    loaderMessage = "Loading...",
}) => {
    // Helper to randomize skeleton widths and counts
    // Helper functions must be inside useMemo or defined outside component to avoid missing dependency warning
    // (Or, define inside useMemo)
    const skeletonLayout = useMemo(() => {
        const getRandom = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
        const getRandomWidth = (min = 40, max = 100) => `${getRandom(min, max)}%`;
        // Hero section
        const hero = [
            { width: getRandomWidth(55, 80), height: 48, className: "mb-4" }, // title
            { width: getRandomWidth(35, 60), height: 24, className: "mb-2" }, // subtitle
            { width: getRandomWidth(20, 40), height: 40, className: "mb-2 rounded-full" }, // button
        ];

        // Paragraphs
        const paraCount = getRandom(3, 6);
        const paragraphs = Array.from({ length: paraCount }, (_, i) => ({
            width: getRandomWidth(50, 100),
            height: 20,
            className: "mb-2",
        }));

        // Cards
        const cardCount = getRandom(2, 4);
        const cards = Array.from({ length: cardCount }, () => ({
            image: { width: "100%", height: getRandom(100, 150), className: "mb-3 rounded-xl" },
            title: { width: getRandomWidth(60, 90), height: 24, className: "mb-2" },
            text: { width: getRandomWidth(50, 80), height: 16, className: undefined },
        }));

        // More paragraphs
        const moreParaCount = getRandom(2, 4);
        const moreParagraphs = Array.from({ length: moreParaCount }, () => ({
            width: getRandomWidth(50, 90),
            height: 20,
            className: "mb-2",
        }));

        return { hero, paragraphs, cards, moreParagraphs };
    }, []);

    return (
        <Section
            {...sectionProps}
            className={className}
            style={{
                minHeight: `calc(100vh - ${headerHeight}px - ${footerHeight}px)`,
                ...sectionProps?.style,
            }}
            padding="none"
            variant={sectionProps.variant || "dark"}
            contained={false}
        >
            <div className="w-full h-full flex flex-col justify-center items-center relative">
                {/* Hero section skeleton */}
                <div className="w-full flex flex-col items-center mb-10">
                    {skeletonLayout.hero.map((item, i) => (
                        <Skeleton
                            key={"hero-" + i}
                            className={item.className}
                            style={{ width: item.width, height: item.height }}
                        />
                    ))}
                </div>

                {/* Paragraph skeletons */}
                <div className="w-full max-w-2xl mb-8">
                    {skeletonLayout.paragraphs.map((item, i) => (
                        <Skeleton
                            key={"para-" + i}
                            className={item.className}
                            style={{ width: item.width, height: item.height }}
                        />
                    ))}
                </div>

                {/* Cards skeleton row */}
                <div className="w-full flex flex-row justify-center gap-6 mb-8">
                    {skeletonLayout.cards.map((card, i) => (
                        <div className="flex flex-col items-center w-60" key={"card-" + i}>
                            <Skeleton className={card.image.className} style={{ width: card.image.width, height: card.image.height }} />
                            <Skeleton className={card.title.className} style={{ width: card.title.width, height: card.title.height }} />
                            <Skeleton style={{ width: card.text.width, height: card.text.height }} />
                        </div>
                    ))}
                </div>

                {/* More paragraphs for realism */}
                <div className="w-full max-w-2xl mb-4">
                    {skeletonLayout.moreParagraphs.map((item, i) => (
                        <Skeleton
                            key={"more-para-" + i}
                            className={item.className}
                            style={{ width: item.width, height: item.height }}
                        />
                    ))}
                </div>
                {/* Overlay loader if requested */}
                {showLoader && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                        <LoadingSkeleton message={loaderMessage} opacity={40} />
                    </div>
                )}
            </div>
        </Section>
    );
};

export default PreloadFill;
